<template>
  <div class="page-layout">
    <div class="page-content page-content--narrow">
      <AppBreadcrumbs :items="breadcrumbItems" />
      <h1>Редактирование трека</h1>

      <div v-if="loading" class="panel">
        <p class="empty-state">Загрузка...</p>
      </div>

      <form v-else class="panel track-form" @submit.prevent="submit">
        <div class="form-field">
          <label for="track-name">Название</label>
          <input id="track-name" v-model="name" type="text" required />
        </div>

        <div class="form-field">
          <label>Описание</label>
          <RichTextEditor
            v-model="description"
            placeholder="Кратко опишите маршрут"
            :allow-images="false"
          />
        </div>

        <TrackPhotoForm
          v-model:pending-photos="pendingPhotos"
          v-model:removed-photo-ids="removedPhotoIds"
          :existing-photos="existingPhotos"
        />

        <TrackDifficultyProfilesForm v-model="difficultyProfiles" />

        <fieldset class="form-field visibility-field">
          <legend>Видимость</legend>
          <label class="radio-label">
            <input v-model="isPublic" type="radio" :value="false" />
            Приватный
          </label>
          <label class="radio-label">
            <input v-model="isPublic" type="radio" :value="true" />
            Публичный
          </label>
        </fieldset>

        <label class="checkbox-label">
          <input v-model="allowComments" type="checkbox" />
          Разрешить комментарии
        </label>

        <p v-if="error" class="error-message">{{ error }}</p>

        <div class="btn-row">
          <button class="btn" type="submit" :disabled="submitting">
            {{ submitting ? 'Сохранение...' : 'Сохранить' }}
          </button>
          <router-link class="btn btn-secondary" :to="{ name: 'track', params: { id: route.params.id } }">
            Отмена
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppBreadcrumbs from '../components/AppBreadcrumbs.vue'
import RichTextEditor from '../components/RichTextEditor.vue'
import TrackDifficultyProfilesForm from '../components/TrackDifficultyProfilesForm.vue'
import TrackPhotoForm from '../components/TrackPhotoForm.vue'
import { fetchTrack, updateTrack } from '../api/tracks'
import { createDefaultDifficultyProfiles } from '../utils/trackFormat'

const route = useRoute()
const router = useRouter()

const loading = ref(true)
const name = ref('')
const description = ref('')
const existingPhotos = ref([])
const pendingPhotos = ref([])
const removedPhotoIds = ref([])
const difficultyProfiles = ref(createDefaultDifficultyProfiles())
const isPublic = ref(false)
const allowComments = ref(false)
const submitting = ref(false)
const error = ref('')

const breadcrumbItems = computed(() => [
  { label: 'Треки', to: { name: 'home' } },
  { label: name.value || 'Трек', to: { name: 'track', params: { id: route.params.id } } },
  { label: 'Редактирование' },
])

async function loadTrack() {
  loading.value = true
  error.value = ''

  try {
    const { data } = await fetchTrack(route.params.id)
    name.value = data.name
    description.value = data.description || ''
    existingPhotos.value = data.photos || []
    difficultyProfiles.value = data.difficulty_profiles?.length
      ? data.difficulty_profiles
      : createDefaultDifficultyProfiles()
    isPublic.value = data.public
    allowComments.value = data.allow_comments
  } catch {
    error.value = 'Не удалось загрузить трек'
  } finally {
    loading.value = false
  }
}

async function submit() {
  submitting.value = true
  error.value = ''

  try {
    const photoOptions = {
      photos: pendingPhotos.value.map((photo) => photo.file),
      photoMetadata: pendingPhotos.value.map((photo) =>
        photo.gps ? { lat: photo.gps.lat, lng: photo.gps.lng } : {}
      ),
      removePhotoIds: removedPhotoIds.value,
    }

    const { data } = await updateTrack(
      route.params.id,
      {
        name: name.value,
        description: description.value,
        difficulty_profiles: difficultyProfiles.value,
        public: isPublic.value,
        allow_comments: allowComments.value,
      },
      photoOptions
    )
    router.push({ name: 'track', params: { id: data.id } })
  } catch (err) {
    error.value = err.response?.data?.errors?.join(', ') || 'Не удалось сохранить трек'
  } finally {
    submitting.value = false
  }
}

onMounted(loadTrack)
</script>

<style scoped>
.track-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.track-form textarea,
.track-form input[type='text'] {
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.visibility-field {
  border: none;
  margin: 0;
  padding: 0;
}

.visibility-field legend {
  margin-bottom: 0.375rem;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}
</style>
